/**
 * Nodals Tag Loader
 *
 * This is the mutable entry point served at /nodals.js with a short CDN TTL.
 * It determines which version of the core to load based on rollout configuration
 * and injects the appropriate script tag.
 *
 * IMPORTANT: Keep this file as small as possible. Every byte counts.
 */

import { TAG_STORAGE_KEY } from '../shared/constants';
import { captureInitialUrl } from '../shared/url';

// Capture initial URL immediately, before any async operations or History API changes
window.$nodals = window.$nodals || {};
window.$nodals.initialUrl = window.$nodals.initialUrl ?? captureInitialUrl();

declare const __ROLLOUT_WEIGHT__: number;
declare const __LIB_VERSION__: string;
declare const __PREVIOUS_STABLE_VERSION__: string;

interface RolloutEntry {
  version: string;
  weight: number;
}

/**
 * Rollout configuration - baked in at build time
 * The current version gets the configured weight, previous stable gets the remainder
 */
const ROLLOUT_CONFIG: RolloutEntry[] =
  __ROLLOUT_WEIGHT__ === 100
    ? [{ version: __LIB_VERSION__, weight: 100 }]
    : [
        { version: __LIB_VERSION__, weight: __ROLLOUT_WEIGHT__ },
        { version: __PREVIOUS_STABLE_VERSION__, weight: 100 - __ROLLOUT_WEIGHT__ },
      ];

interface TagStorageData {
  cohort?: string;
  rolloutHash?: string;
}

function getStoredData(): TagStorageData {
  try {
    const raw = localStorage.getItem(TAG_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setStoredData(data: TagStorageData): void {
  try {
    localStorage.setItem(TAG_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable - proceed without persistence
  }
}

/**
 * Generate a hash of the current rollout configuration
 * Changes when versions or weights change, triggering re-assignment
 */
function getRolloutHash(): string {
  return ROLLOUT_CONFIG.map((entry) => `${entry.version}:${entry.weight}`).join(',');
}

function assignCohort(): string {
  const data = getStoredData();
  const currentHash = getRolloutHash();

  // Check if user has an assignment and the rollout config hasn't changed
  if (data.cohort && data.rolloutHash === currentHash) {
    // Rollout config is the same, keep existing assignment
    return data.cohort;
  }

  // Rollout config changed or no assignment - assign new cohort based on weights
  const rand = Math.random() * 100;
  let cumulative = 0;

  for (const entry of ROLLOUT_CONFIG) {
    cumulative += entry.weight;
    if (rand < cumulative) {
      data.cohort = entry.version;
      data.rolloutHash = currentHash;
      setStoredData(data);
      return entry.version;
    }
  }

  // Fallback to first entry (should not happen if weights sum to 100)
  const fallback = ROLLOUT_CONFIG[0]?.version || __LIB_VERSION__;
  data.cohort = fallback;
  data.rolloutHash = currentHash;
  setStoredData(data);
  return fallback;
}

/**
 * Determines the base URL by inspecting the current script's source
 * Falls back to production CDN if detection fails
 */
function getBaseUrl(): string {
  const currentScript = document.currentScript as HTMLScriptElement;

  if (currentScript && currentScript.src) {
    try {
      const url = new URL(currentScript.src);
      // Extract the base path up to nodals.js
      // e.g., https://static.nodals.io/lib/tag/nodals.js -> https://static.nodals.io/lib/tag/
      const pathname = url.pathname;
      const basePath = pathname.substring(0, pathname.lastIndexOf('/') + 1);
      return `${url.origin}${basePath}`;
    } catch {
      // URL parsing failed, fall through to default
    }
  }

  // Default to production CDN
  return 'https://static.nodals.io/lib/tag/';
}

function injectCoreScript(version: string): void {
  const baseUrl = getBaseUrl();
  const script = document.createElement('script');
  script.async = true;
  script.src = `${baseUrl}v${version}/nodals.core.js`;

  // Insert as early as possible in <head>
  const head = document.head || document.getElementsByTagName('head')[0];
  if (head.firstChild) {
    head.insertBefore(script, head.firstChild);
  } else {
    head.appendChild(script);
  }
}

// Execute loader
const version = assignCohort();
injectCoreScript(version);
